import React, { useState } from "react";

// ─── CHALLENGE METADATA ──────────────────────────────────────────────────────

const CHALLENGE = {
  id: "animals_big_and_small_v2",
  title: "Animals Big and Small",
  subject: "math",
  grade_level: "4",
  domain: "Number and Operations in Base Ten / Measurement and Data",
  total_points: 21,
  bonus_points: 5,
  total_with_bonus: 17, // main challenge scored out of 12, bonus out of 5, combined 17
  standards: ["4.NBT.A.2", "4.MD.A.1", "4.MD.A.2"],
};

// ─── FUTURE READY SKILLS ─────────────────────────────────────────────────────

const SKILLS = {
  grit: { name: "Grit", num: 1, color: "#DC2626", maxPts: 5 },
  reason: { name: "Ability to Reason", num: 2, color: "#2563EB", maxPts: 15 },
  construct_arguments: { name: "Construct Viable Arguments", num: 3, color: "#059669", maxPts: 10 },
  precision: { name: "Work Precisely", num: 5, color: "#D97706", maxPts: 8 },
  model_analyze: { name: "Model, Analyze & Predict", num: 6, color: "#7C3AED", maxPts: 11 },
  structure: { name: "Recognize Structure", num: 7, color: "#0891B2", maxPts: 5 },
  communicate: { name: "Communicate Effectively", num: 8, color: "#DB2777", maxPts: 3 },
  write: { name: "Write Coherently", num: 9, color: "#9333EA", maxPts: 1 },
  collaborate: { name: "Collaborate", num: 10, color: "#EA580C", maxPts: 3 },
  flexible: { name: "Think Flexibly", num: 11, color: "#65A30D", maxPts: 1 },
  decisions: { name: "Make Decisions", num: 12, color: "#0D9488", maxPts: 2 },
  creative: { name: "Creative & Entrepreneurial", num: 13, color: "#E11D48", maxPts: 2 },
};

// ─── QUESTION-LEVEL DATA WITH SIMULATED STUDENT RESPONSES ────────────────────

const QUESTIONS = [
  {
    id: "Q1", prompt: "What is Alejandro's weight?",
    dok: 1, max_score: 1, type: "select",
    correct_answer: "Five thousands, two hundreds, three tens and eight ones",
    standards: ["4.NBT.A.2"],
    skills: ["precision", "structure", "communicate"],
    student_score: 1,
    student_response: "Selected: Five thousands, two hundreds, three tens and eight ones",
    confidence: "high",
    evidence: "Correct selection. Student demonstrates place value understanding.",
  },
  {
    id: "Q2", prompt: "Which way of showing Alejandro's weight helps you better understand the number?",
    dok: 2, max_score: 1, type: "open_response",
    correct_answer: "Varies — student should explain reasoning for their choice",
    standards: [],
    skills: ["communicate", "reason", "decisions"],
    student_score: 1,
    student_response: "I think 5,238 is easier because thats how we write numbers in math class and I can see its five thousand right away.",
    confidence: "high",
    evidence: "Clear preference stated with reasoning. References familiarity and readability.",
  },
  {
    id: "Q3", prompt: "Male animals are bigger than female animals. Do you think this statement is true?",
    dok: 2, max_score: 1, type: "open_response",
    correct_answer: "Varies — student should agree or disagree with reasoning",
    standards: [],
    skills: ["construct_arguments", "flexible", "reason"],
    student_score: 0,
    student_response: "Yes I think so.",
    confidence: "high",
    evidence: "Student agrees but provides no reasoning or explanation. Response is incomplete — lacks the 'why' required by the rubric.",
  },
  {
    id: "Q4", prompt: "Drag the symbol that works below. (Black Mamba lengths)",
    dok: 1, max_score: 1, type: "drag_drop",
    correct_answer: "13 feet = 13 feet",
    standards: ["4.NBT.A.2"],
    skills: ["precision", "reason", "structure"],
    student_score: 1,
    student_response: "13 = 13 ✓",
    confidence: "high",
    evidence: "Correct symbol placed. Student recognizes equality.",
  },
  {
    id: "Q5", prompt: "Based on the length, should you feed a male black mamba the same, more, or less than a female? Explain.",
    dok: 2, max_score: 2, type: "open_response",
    correct_answer: "Both should receive similar food because they are similar lengths/size",
    standards: ["4.MD.A.1"],
    skills: ["reason", "model_analyze", "construct_arguments"],
    student_score: 1,
    student_response: "They should get the same food because they are the same length.",
    confidence: "medium",
    evidence: "Correct conclusion (same food) and references Q4 comparison. However, explanation is thin — 'because they are the same length' restates the comparison without extending reasoning to why similar length implies similar food needs. Scored 1/2: describes food amount OR explains, not both.",
  },
  {
    id: "Q6", prompt: "Drag the symbol that works below. (Mako shark weights)",
    dok: 1, max_score: 1, type: "drag_drop",
    correct_answer: "280 pounds < 298 pounds",
    standards: ["4.NBT.A.2"],
    skills: ["precision", "reason", "structure"],
    student_score: 1,
    student_response: "280 < 298 ✓",
    confidence: "high",
    evidence: "Correct inequality symbol. Student compares two numbers accurately.",
  },
  {
    id: "Q7", prompt: "Based on the weight, which needs more food, male or female mako sharks? Explain.",
    dok: 2, max_score: 2, type: "open_response",
    correct_answer: "Females need more food because they weigh more (298 > 280)",
    standards: ["4.MD.A.1"],
    skills: ["reason", "model_analyze", "construct_arguments"],
    student_score: 1,
    student_response: "Females need more food because they are bigger.",
    confidence: "medium",
    evidence: "Correct conclusion but explanation lacks specificity. Says 'bigger' without referencing the actual weight comparison from Q6. Scored 1/2: accurate conclusion OR explanation, not both.",
  },
  {
    id: "Q8", prompt: "Drag the weights for male and female humpback whales and the correct symbol.",
    dok: 2, max_score: 1, type: "drag_drop",
    correct_answer: "65,400 < 71,500",
    standards: ["4.NBT.A.2"],
    skills: ["precision", "reason", "structure"],
    student_score: 1,
    student_response: "65,400 < 71,500 ✓",
    confidence: "high",
    evidence: "Correct conversion from word form to numerals and correct inequality. Demonstrates large number comparison.",
  },
  {
    id: "Q9", prompt: "Why do you think female humpback whales may be larger than male humpback whales?",
    dok: 2, max_score: 1, type: "open_response",
    correct_answer: "Varies — hypothesis explaining why (e.g., females carry babies)",
    standards: ["4.MD.A.1"],
    skills: ["construct_arguments", "reason", "creative"],
    student_score: 1,
    student_response: "I think female whales are bigger because they have to carry baby whales inside them and feed them milk so they need to be big.",
    confidence: "high",
    evidence: "Strong hypothesis with two supporting reasons (carrying babies + nursing). Shows creative thinking and inductive reasoning.",
  },
  {
    id: "Q10", prompt: "Drag the symbol that works below. (Octopus weights)",
    dok: 1, max_score: 1, type: "drag_drop",
    correct_answer: "22 pounds < 27 pounds",
    standards: ["4.NBT.A.2"],
    skills: ["precision", "reason", "structure"],
    student_score: 1,
    student_response: "22 < 27 ✓",
    confidence: "high",
    evidence: "Correct symbol. Consistent accuracy on comparison tasks.",
  },
  {
    id: "Q11", prompt: "Based on the weight, which needs more food, a male or female octopus? Explain.",
    dok: 2, max_score: 2, type: "open_response",
    correct_answer: "Females need more food because they weigh more",
    standards: ["4.MD.A.1"],
    skills: ["reason", "model_analyze", "construct_arguments"],
    student_score: 1,
    student_response: "Female octopus needs more food because she weighs more than the male.",
    confidence: "medium",
    evidence: "Correct conclusion referencing the weight comparison. But lacks the explicit connection: doesn't reference the specific numbers (22 vs 27) or the Q10 comparison. Pattern: student consistently scores 1/2 on explain-and-describe questions — gets the 'what' but not the 'and why' at the level of detail the rubric requires.",
  },
  {
    id: "Q12", prompt: "Which one do you want to learn more about?",
    dok: 0, max_score: 1, type: "select",
    correct_answer: "Any animal selection",
    standards: [],
    skills: ["grit", "decisions", "creative"],
    student_score: 1,
    student_response: "Selected: Polar Bear",
    confidence: "high",
    evidence: "Student made a selection, demonstrating engagement and curiosity.",
  },
  {
    id: "Q13", prompt: "For the animal you picked, fill out the weights and heights for both males and females.",
    dok: 1, max_score: 3, type: "table_entry",
    correct_answer: "Accurate data from research with correct units",
    standards: ["4.MD.A.2"],
    skills: ["model_analyze", "precision", "grit", "collaborate"],
    student_score: 2,
    student_response: "Male: 990 lbs, 4.4 ft | Female: 330-550 lbs, 5.9 ft (height entries missing units on two cells)",
    confidence: "medium",
    evidence: "Data is largely accurate for polar bears. Two cells missing units (counted as 2 errors). Weight ranges used for female instead of single value — acceptable but noted. Scored 2/3 (2-3 errors).",
  },
  {
    id: "Q14", prompt: "What is an interesting fact about your animal?",
    dok: 1, max_score: 1, type: "open_response",
    correct_answer: "Any relevant fact from research",
    standards: [],
    skills: ["communicate", "write", "grit"],
    student_score: 1,
    student_response: "Polar bears can swim for days without stopping and their fur is actually clear not white. The light makes it look white.",
    confidence: "high",
    evidence: "Two interesting facts shared with accurate information. Demonstrates research engagement and effective communication.",
  },
  {
    id: "Q15", prompt: "With your animal, who will need more food: male or female? Describe why.",
    dok: 2, max_score: 2, type: "open_response",
    correct_answer: "Based on student's Q13 data — male polar bears are larger, need more food",
    standards: [],
    skills: ["reason", "model_analyze", "construct_arguments"],
    student_score: 1,
    student_response: "Males need more food because they are way bigger.",
    confidence: "medium",
    evidence: "Correct conclusion given Q13 data (990 lbs vs 330-550 lbs). But again: explanation uses general language ('way bigger') rather than referencing specific data. Consistent pattern with Q5, Q7, Q11.",
  },
];

// ─── COMPUTED SKILL SCORES ───────────────────────────────────────────────────

function computeSkillScores(questions) {
  const skillPoints = {};
  Object.keys(SKILLS).forEach(k => { skillPoints[k] = { earned: 0, possible: 0, questions: [] }; });
  questions.forEach(q => {
    q.skills.forEach(sk => {
      if (skillPoints[sk]) {
        skillPoints[sk].earned += q.student_score;
        skillPoints[sk].possible += q.max_score;
        skillPoints[sk].questions.push(q.id);
      }
    });
  });
  return skillPoints;
}

function getRating(earned, maxPts, skillId) {
  // Use the rubric-defined thresholds
  const thresholds = {
    grit: { exceeded: [5,5], met: [4,4], nearly: [3,3] },
    reason: { exceeded: [13,15], met: [10,12], nearly: [6,9] },
    construct_arguments: { exceeded: [9,10], met: [7,8], nearly: [4,6] },
    precision: { exceeded: [7,8], met: [6,6], nearly: [4,5] },
    model_analyze: { exceeded: [10,11], met: [8,9], nearly: [5,7] },
    structure: { exceeded: [5,5], met: [4,4], nearly: [3,3] },
    communicate: { exceeded: [3,3], met: [2,2], nearly: [-1,-1] },
    write: { exceeded: [1,1], met: [-1,-1], nearly: [-1,-1] },
    collaborate: { exceeded: [3,3], met: [2,2], nearly: [-1,-1] },
    flexible: { exceeded: [1,1], met: [-1,-1], nearly: [-1,-1] },
    decisions: { exceeded: [2,2], met: [-1,-1], nearly: [1,1] },
    creative: { exceeded: [2,2], met: [-1,-1], nearly: [1,1] },
  };
  const t = thresholds[skillId];
  if (!t) return "N/A";
  if (earned >= t.exceeded[0] && earned <= t.exceeded[1]) return "Exceeded";
  if (earned >= t.met[0] && earned <= t.met[1]) return "Met";
  if (earned >= t.nearly[0] && earned <= t.nearly[1]) return "Nearly Met";
  return "Did Not Meet";
}

function getOverallRating(total) {
  if (total >= 19) return "Exceeded";
  if (total >= 15) return "Met";
  if (total >= 11) return "Nearly Met";
  return "Did Not Meet";
}

// ─── TEACHER GUIDANCE PER SKILL ──────────────────────────────────────────────

const TEACHER_GUIDANCE = {
  construct_arguments: {
    priority: "high",
    focus: "Evidence-Based Explanation Depth",
    recommendation: "This student consistently reaches correct conclusions but does not provide the level of explanation the rubric requires for full credit. Across Q5, Q7, Q11, and Q15, the pattern is identical: right answer, thin reasoning. This is not a comprehension gap — the student understands the math. It is an explanation habit. The student needs structured practice in the Claim-Evidence-Reasoning (CER) framework, specifically the 'Evidence' step where they reference specific numbers or data from the problem.",
    activities: [
      "CER sentence frame practice: 'I think ___ because in Q___, we saw that ___ which means ___'",
      "Highlight-and-extend: return Q5 and Q7, ask student to underline their claim, then add one sentence of specific evidence",
      "Partner explanation protocol: student explains answer verbally to a peer who asks 'How do you know?' forcing the evidence step"
    ],
    rubric_move: "Consistent 1/2 → 2/2 on explanation questions requires referencing specific data from prior questions and connecting comparison to conclusion explicitly.",
  },
  reason: {
    priority: "medium",
    focus: "Quantitative Specificity in Reasoning",
    recommendation: "Strong procedural reasoning (perfect on all comparison operations Q4, Q6, Q8, Q10) but reasoning-in-words lags behind reasoning-in-symbols. The student can manipulate numbers correctly but defaults to general language ('bigger,' 'more') in explanations rather than quantitative language ('298 is 18 more than 280'). Bridge the gap between symbolic and verbal mathematical reasoning.",
    activities: [
      "Number talk warm-ups: show two numbers, students must explain the comparison using exact differences",
      "Sentence starter: 'The difference between ___ and ___ is ___, which tells me ___'",
      "Comparison journal: for each drag-and-drop question, student writes one sentence explaining what the symbol means in words before moving on"
    ],
    rubric_move: "Student already meets proficiency on procedural reasoning. Growth edge is verbal articulation of quantitative relationships.",
  },
  precision: {
    priority: "medium",
    focus: "Unit Inclusion in Data Recording",
    recommendation: "Strong precision on comparison tasks (Q1, Q4, Q6, Q8, Q10 all correct). The gap is in Q13 where two cells were missing units. This is a common 4th-grade oversight — the student knows the units but doesn't consistently record them. Make unit inclusion a non-negotiable habit in all data recording tasks.",
    activities: [
      "Unit check routine: before submitting any table, student circles every cell and checks for a unit label",
      "Color-code protocol: numbers in blue, units in red — visual separation builds the habit",
      "Peer audit: partners swap tables and flag any cell missing units before submission"
    ],
    rubric_move: "Score 2 → 3 on Q13 requires 0-1 errors. Currently at 2-3 errors, all unit-related.",
  },
  model_analyze: {
    priority: "medium",
    focus: "Connecting Data to Real-World Conclusions",
    recommendation: "The student successfully applies mathematical comparisons to feeding scenarios but doesn't go beyond the direct comparison. In Q5, Q7, Q11, and Q15, the student makes the right prediction but doesn't elaborate on why weight or length would relate to food needs. Encourage thinking about the underlying relationship, not just the mathematical outcome.",
    activities: [
      "Why-chain exercise: after answering 'who needs more food,' ask 'why would a heavier animal need more food?' — push for biological reasoning",
      "Real-world connection prompts: 'What else in real life works this way? (Bigger car = more gas, bigger house = more electricity)'",
      "Data story: student writes a 3-sentence 'story' of what the numbers tell us about the animal"
    ],
    rubric_move: "Currently earning 5/11. Growth requires linking mathematical comparisons to real-world reasoning explicitly.",
  },
  communicate: {
    priority: "low",
    focus: "Enrichment — Already Strong",
    recommendation: "Communication is a strength for this student. Q2 shows clear preference articulation with reasoning, Q14 shows engaging factual writing (two facts with detail about polar bear fur). No intervention needed — reinforce and celebrate.",
    activities: [
      "Ask this student to model good explanations for peers",
      "Have them write 'example answers' that can be used as anchor responses"
    ],
    rubric_move: "At ceiling (3/3). Enrichment only.",
  },
  grit: {
    priority: "low",
    focus: "Sustained Engagement Confirmed",
    recommendation: "Student completed all questions including bonus research task. Selected polar bear, found accurate data, and shared two interesting facts. Engagement indicators are strong throughout the challenge. No intervention needed.",
    activities: [],
    rubric_move: "Demonstrating persistence. Continue providing research-based challenges.",
  },
  structure: {
    priority: "low",
    focus: "Place Value and Comparison Structure — Solid",
    recommendation: "Perfect scores on all structure-dependent questions (Q1, Q4, Q6, Q8, Q10). Student understands base-ten structure, equality, and inequality. This is a consistent strength.",
    activities: [],
    rubric_move: "At or near ceiling. No intervention required.",
  },
  flexible: {
    priority: "high",
    focus: "Considering Alternative Perspectives",
    recommendation: "Q3 is the only Flexible Thinking assessment point and the student scored 0. The response 'Yes I think so' accepts the statement without considering exceptions or nuance. This is a critical thinking gap — the student defaults to agreement rather than questioning. This matters beyond this challenge: the disposition to question generalizations is foundational to mathematical reasoning.",
    activities: [
      "Always/Sometimes/Never protocol: give statements and require students to categorize them with examples for each",
      "Counter-example practice: 'Can you think of one animal where the female is bigger than the male?'",
      "Devil's advocate pairs: one student argues 'true,' the other argues 'not always true,' then they switch"
    ],
    rubric_move: "Score 0 → 1 requires any reasoning at all. Start with the habit of providing a reason, then build toward nuanced responses.",
  },
};

// ─── CLASS AGGREGATE (SIMULATED) ─────────────────────────────────────────────

const CLASS_AGGREGATE = {
  teacher: "Mr. Okonkwo",
  class_name: "4th Grade Math — Room 12",
  student_count: 24,
  time_period: "January 2026",
  overall: { average: 14.2, distribution: { exceeded: 3, met: 9, nearly: 8, did_not: 4 } },
  skills: [
    { id: "reason", avg: 9.8, max: 15, below: 6, at: 12, above: 6, trend: "+0.4",
      guidance: "Class-wide pattern: procedural comparison is strong but verbal reasoning lags. 6 students below proficiency are primarily losing points on explanation questions (Q5, Q7, Q11, Q15). Implement a whole-class CER mini-lesson series focused on mathematical explanations before the next challenge." },
    { id: "construct_arguments", avg: 5.4, max: 10, below: 10, at: 9, above: 5, trend: "+0.2",
      guidance: "Highest-leverage intervention: 10 students below proficiency. Most common failure mode is identical to the individual student pattern — correct conclusions with insufficient evidence. Introduce argument sentence frames class-wide and post them as an anchor chart." },
    { id: "precision", avg: 6.1, max: 8, below: 4, at: 11, above: 9, trend: "+0.1",
      guidance: "Precision is a class strength. The 4 below-proficiency students are inconsistent on large number comparisons (Q8). Targeted small-group work on 5-digit number comparison would be more efficient than whole-class instruction." },
    { id: "model_analyze", avg: 6.2, max: 11, below: 8, at: 10, above: 6, trend: "-0.2",
      guidance: "Trending downward. Students are accurate on the mathematical operations but struggle to connect comparisons to real-world predictions. Add 'What does this number tell us about the real world?' as a standard follow-up question in all future challenges." },
    { id: "structure", avg: 4.2, max: 5, below: 3, at: 8, above: 13, trend: "+0.3",
      guidance: "Strong class performance on structure recognition. 13 students at 'Exceeded.' The 3 below-proficiency students need targeted place value support — consider a pull-out group during the next challenge." },
    { id: "flexible", avg: 0.5, max: 1, below: 12, at: 0, above: 12, trend: "0.0",
      guidance: "Bimodal distribution: half the class scored 0, half scored 1. Students who scored 0 all gave agreement without reasoning. This is a classroom culture opportunity — build the norm that 'always/never' statements should be questioned. Start each math class with an Always/Sometimes/Never warm-up for the next month." },
  ],
};

// ─── COMPONENTS ──────────────────────────────────────────────────────────────

const Badge = ({ text, bg, color, border }) => (
  <span style={{
    display: "inline-block", padding: "2px 10px", borderRadius: "99px",
    fontSize: "10px", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase",
    background: bg, color, border: `1px solid ${border}`,
  }}>{text}</span>
);

const RatingBadge = ({ rating }) => {
  const m = {
    "Exceeded": { bg: "#ECFDF5", c: "#065F46", b: "#A7F3D0" },
    "Met": { bg: "#EFF6FF", c: "#1E40AF", b: "#BFDBFE" },
    "Nearly Met": { bg: "#FFFBEB", c: "#92400E", b: "#FDE68A" },
    "Did Not Meet": { bg: "#FEF2F2", c: "#991B1B", b: "#FECACA" },
  };
  const s = m[rating] || m["Did Not Meet"];
  return <Badge text={rating} bg={s.bg} color={s.c} border={s.b} />;
};

const ScoreBar = ({ score, max, color }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
    <div style={{ flex: 1, height: "7px", borderRadius: "4px", background: "#E5E7EB", overflow: "hidden" }}>
      <div style={{ width: `${(score / max) * 100}%`, height: "100%", borderRadius: "4px", background: color, transition: "width 0.5s ease" }} />
    </div>
    <span style={{ fontSize: "13px", fontWeight: 700, color: "#1F2937", fontVariantNumeric: "tabular-nums", minWidth: "36px" }}>
      {score}/{max}
    </span>
  </div>
);

const QuestionCard = ({ q, expanded, onToggle }) => {
  const pct = q.max_score > 0 ? q.student_score / q.max_score : 0;
  const barColor = pct >= 1 ? "#059669" : pct >= 0.5 ? "#D97706" : "#DC2626";
  return (
    <div style={{
      background: "#FFF", borderRadius: "10px", border: "1px solid #E5E7EB", overflow: "hidden",
      boxShadow: expanded ? "0 2px 12px rgba(0,0,0,0.06)" : "none", transition: "box-shadow 0.2s",
    }}>
      <div onClick={onToggle} style={{
        padding: "14px 18px", cursor: "pointer", display: "flex", alignItems: "center", gap: "14px",
      }}>
        <div style={{
          width: "34px", height: "34px", borderRadius: "8px", flexShrink: 0,
          background: pct >= 1 ? "#ECFDF5" : pct >= 0.5 ? "#FFFBEB" : "#FEF2F2",
          border: `1px solid ${pct >= 1 ? "#A7F3D0" : pct >= 0.5 ? "#FDE68A" : "#FECACA"}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "13px", fontWeight: 800, color: barColor,
        }}>{q.id}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: "13px", fontWeight: 600, color: "#111827", marginBottom: "4px", lineHeight: 1.4 }}>
            {q.prompt.length > 80 ? q.prompt.slice(0, 80) + "…" : q.prompt}
          </div>
          <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
            {q.skills.map(sk => (
              <span key={sk} style={{
                fontSize: "9px", fontWeight: 600, padding: "1px 6px", borderRadius: "3px",
                background: `${SKILLS[sk].color}12`, color: SKILLS[sk].color,
              }}>{SKILLS[sk].name}</span>
            ))}
          </div>
        </div>
        <div style={{ textAlign: "right", minWidth: "50px" }}>
          <div style={{ fontSize: "16px", fontWeight: 800, color: barColor }}>{q.student_score}/{q.max_score}</div>
          <div style={{ fontSize: "9px", color: "#9CA3AF", textTransform: "uppercase" }}>DOK {q.dok}</div>
        </div>
        <span style={{
          fontSize: "16px", color: "#9CA3AF", transform: expanded ? "rotate(180deg)" : "rotate(0)",
          transition: "transform 0.2s",
        }}>▾</span>
      </div>
      {expanded && (
        <div style={{ padding: "0 18px 18px", borderTop: "1px solid #F3F4F6" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginTop: "14px" }}>
            <div style={{ padding: "10px 14px", background: "#F9FAFB", borderRadius: "6px" }}>
              <div style={{ fontSize: "10px", fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>Student Response</div>
              <div style={{ fontSize: "12px", color: "#374151", lineHeight: 1.5, fontStyle: "italic" }}>"{q.student_response}"</div>
            </div>
            <div style={{ padding: "10px 14px", background: "#F9FAFB", borderRadius: "6px" }}>
              <div style={{ fontSize: "10px", fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>Expected Response</div>
              <div style={{ fontSize: "12px", color: "#374151", lineHeight: 1.5 }}>{q.correct_answer}</div>
            </div>
          </div>
          <div style={{
            marginTop: "10px", padding: "10px 14px", borderRadius: "6px",
            borderLeft: `3px solid ${barColor}`, background: `${barColor}08`,
          }}>
            <div style={{ fontSize: "10px", fontWeight: 700, color: barColor, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>Engine Evidence</div>
            <div style={{ fontSize: "12px", color: "#374151", lineHeight: 1.6 }}>{q.evidence}</div>
          </div>
          {q.standards.length > 0 && (
            <div style={{ marginTop: "8px", display: "flex", gap: "6px", alignItems: "center" }}>
              <span style={{ fontSize: "10px", color: "#9CA3AF", fontWeight: 600 }}>Standards:</span>
              {q.standards.map(s => (
                <span key={s} style={{ fontSize: "10px", padding: "1px 6px", borderRadius: "3px", background: "#F3F4F6", color: "#6B7280", fontWeight: 600 }}>{s}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TeacherGuidancePanel = ({ guidance, skillName, color }) => (
  <div style={{
    padding: "16px 18px", background: "#FFFBEB", borderRadius: "8px",
    border: "1px solid #FDE68A", borderLeft: `4px solid ${color}`,
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
      <span style={{ fontSize: "14px" }}>&#9881;</span>
      <span style={{ fontSize: "12px", fontWeight: 700, color: "#78350F" }}>{skillName}</span>
      <span style={{
        marginLeft: "auto", fontSize: "9px", fontWeight: 700, padding: "2px 8px", borderRadius: "99px",
        background: guidance.priority === "high" ? "#FEE2E2" : guidance.priority === "medium" ? "#FEF3C7" : "#D1FAE5",
        color: guidance.priority === "high" ? "#991B1B" : guidance.priority === "medium" ? "#92400E" : "#065F46",
        textTransform: "uppercase",
      }}>{guidance.priority} priority</span>
    </div>
    <div style={{ fontSize: "11px", fontWeight: 700, color: "#92400E", marginBottom: "4px" }}>Focus: {guidance.focus}</div>
    <p style={{ margin: "0 0 10px", fontSize: "12px", color: "#451A03", lineHeight: 1.6 }}>{guidance.recommendation}</p>
    {guidance.activities.length > 0 && (
      <>
        <div style={{ fontSize: "10px", fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>Suggested Activities</div>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {guidance.activities.map((a, i) => (
            <div key={i} style={{
              padding: "6px 10px", background: "#FEF3C7", borderRadius: "5px",
              fontSize: "11px", color: "#451A03", lineHeight: 1.5,
              display: "flex", gap: "6px",
            }}>
              <span style={{ fontWeight: 700, color: "#B45309", flexShrink: 0 }}>{i + 1}.</span>
              <span>{a}</span>
            </div>
          ))}
        </div>
      </>
    )}
    <div style={{
      marginTop: "8px", padding: "6px 10px", borderRadius: "5px",
      background: "#FFFDE7", border: "1px dashed #D97706",
      fontSize: "11px", color: "#78350F", fontWeight: 600,
    }}>{guidance.rubric_move}</div>
  </div>
);

// ─── MAIN ────────────────────────────────────────────────────────────────────

export default function AnimalsChallengeDashboard() {
  const [view, setView] = useState("questions");
  const [expandedQ, setExpandedQ] = useState("Q3");
  const [showJson, setShowJson] = useState(false);
  const [showGuidance, setShowGuidance] = useState(true);

  const skillScores = computeSkillScores(QUESTIONS);
  const totalEarned = QUESTIONS.reduce((s, q) => s + q.student_score, 0);
  const totalPossible = QUESTIONS.reduce((s, q) => s + q.max_score, 0);
  const overallRating = getOverallRating(totalEarned);

  const tab = (label, val) => ({
    padding: "7px 18px", borderRadius: "7px", border: "none", cursor: "pointer",
    fontSize: "12px", fontWeight: 600, transition: "all 0.2s",
    background: view === val ? "#111827" : "transparent",
    color: view === val ? "#FFF" : "#6B7280",
  });

  // Build JSON output for the student
  const studentJson = {
    assessment_id: "assess_animals_20260118_0015",
    student_id: "STU-4419",
    challenge_id: CHALLENGE.id,
    challenge_title: CHALLENGE.title,
    timestamp: "2026-01-18T14:22:00Z",
    artifacts_ingested: [
      { artifact_id: "art_001", type: "challenge_responses", format: "application/json", label: "Challenge Responses (Q1-Q15)", description: "Student responses captured within Mindsets platform, including drag-drop interactions, text entries, and table data" },
      { artifact_id: "art_002", type: "behavioral_log", format: "application/json", label: "Session Behavioral Log", description: "Timestamps, navigation patterns, revision history, time-on-task per question", duration_seconds: 1847 },
    ],
    overall: { score: totalEarned, max_score: totalPossible, rating: overallRating },
    questions: QUESTIONS.map(q => ({
      question_id: q.id, prompt: q.prompt, score: q.student_score, max_score: q.max_score,
      confidence: q.confidence, skills_assessed: q.skills, evidence: q.evidence,
      student_response: q.student_response,
    })),
    skills_summary: Object.entries(skillScores).map(([k, v]) => ({
      skill_id: k, skill_name: SKILLS[k].name, earned: v.earned, possible: v.possible,
      rating: getRating(v.earned, SKILLS[k].maxPts, k),
      questions: v.questions,
      teacher_guidance: TEACHER_GUIDANCE[k] || null,
    })),
  };

  return (
    <div style={{
      fontFamily: "'Instrument Sans', 'DM Sans', 'Segoe UI', system-ui, sans-serif",
      background: "#F8F9FB", minHeight: "100vh",
    }}>
      {/* Header */}
      <div style={{ background: "#111827", padding: "24px 28px 18px", borderBottom: "3px solid #059669" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "#34D399", marginBottom: "6px" }}>
              Mindsets Intelligence Engine — Challenge Output
            </div>
            <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: "#F9FAFB" }}>
              Animals Big and Small
            </h1>
            <div style={{ fontSize: "12px", color: "#9CA3AF", marginTop: "4px" }}>
              Grade {CHALLENGE.grade_level} · {CHALLENGE.domain} · {CHALLENGE.standards.join(", ")} · {QUESTIONS.length} questions
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "28px", fontWeight: 800, color: "#F9FAFB", lineHeight: 1 }}>{totalEarned}/{totalPossible}</div>
            <div style={{ marginTop: "4px" }}><RatingBadge rating={overallRating} /></div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div style={{
        padding: "12px 28px", display: "flex", alignItems: "center", gap: "12px",
        borderBottom: "1px solid #E5E7EB", background: "#FFF", flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", gap: "3px", background: "#F3F4F6", padding: "3px", borderRadius: "9px" }}>
          <button style={tab("Questions", "questions")} onClick={() => setView("questions")}>Questions</button>
          <button style={tab("Skills", "skills")} onClick={() => setView("skills")}>Skill Summary</button>
          <button style={tab("Class", "class")} onClick={() => setView("class")}>Class Aggregate</button>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
          <button onClick={() => setShowGuidance(!showGuidance)} style={{
            padding: "6px 14px", borderRadius: "6px", border: "1px solid #FDE68A",
            background: showGuidance ? "#FFFBEB" : "#FFF", color: showGuidance ? "#92400E" : "#9CA3AF",
            fontSize: "11px", fontWeight: 600, cursor: "pointer",
          }}>&#9881; Teacher Guidance {showGuidance ? "ON" : "OFF"}</button>
          <button onClick={() => setShowJson(!showJson)} style={{
            padding: "6px 14px", borderRadius: "6px", border: "1px solid #D1D5DB",
            background: showJson ? "#111827" : "#FFF", color: showJson ? "#FFF" : "#374151",
            fontSize: "11px", fontWeight: 600, cursor: "pointer",
          }}>{showJson ? "◉" : "○"} Raw JSON</button>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "20px 28px", maxWidth: "980px" }}>

        {/* ── QUESTIONS VIEW ── */}
        {view === "questions" && (
          <>
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "16px",
            }}>
              <div style={{ padding: "12px 16px", background: "#FFF", borderRadius: "8px", border: "1px solid #E5E7EB" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", marginBottom: "3px" }}>Student</div>
                <div style={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}>STU-4419</div>
                <div style={{ fontSize: "11px", color: "#6B7280" }}>Grade 4 · Mr. Okonkwo — Room 12</div>
              </div>
              <div style={{ padding: "12px 16px", background: "#FFF", borderRadius: "8px", border: "1px solid #E5E7EB" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", marginBottom: "3px" }}>Artifacts Ingested</div>
                <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
                  <span style={{ fontSize: "10px", padding: "3px 8px", borderRadius: "4px", background: "#EFF6FF", color: "#1E40AF", fontWeight: 600 }}>15 Challenge Responses</span>
                  <span style={{ fontSize: "10px", padding: "3px 8px", borderRadius: "4px", background: "#F5F3FF", color: "#5B21B6", fontWeight: 600 }}>Behavioral Log (30:47)</span>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {QUESTIONS.map(q => (
                <QuestionCard key={q.id} q={q} expanded={expandedQ === q.id} onToggle={() => setExpandedQ(expandedQ === q.id ? null : q.id)} />
              ))}
            </div>

            {showGuidance && (
              <div style={{ marginTop: "20px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "10px" }}>
                  Teacher Guidance — Question Patterns Detected
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {["construct_arguments", "flexible", "precision"].map(sk => (
                    TEACHER_GUIDANCE[sk] && <TeacherGuidancePanel key={sk} guidance={TEACHER_GUIDANCE[sk]} skillName={SKILLS[sk].name} color={SKILLS[sk].color} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── SKILL SUMMARY VIEW ── */}
        {view === "skills" && (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {Object.entries(skillScores)
                .filter(([, v]) => v.possible > 0)
                .sort((a, b) => b[1].possible - a[1].possible)
                .map(([sk, v]) => {
                  const meta = SKILLS[sk];
                  const rating = getRating(v.earned, meta.maxPts, sk);
                  return (
                    <div key={sk} style={{
                      padding: "16px 18px", background: "#FFF", borderRadius: "10px",
                      border: "1px solid #E5E7EB",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
                        <div style={{
                          width: "30px", height: "30px", borderRadius: "7px", background: `${meta.color}14`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: "12px", fontWeight: 800, color: meta.color, flexShrink: 0,
                        }}>{meta.num}</div>
                        <span style={{ fontSize: "14px", fontWeight: 600, color: "#111827", flex: 1 }}>{meta.name}</span>
                        <RatingBadge rating={rating} />
                      </div>
                      <ScoreBar score={v.earned} max={v.possible} color={meta.color} />
                      <div style={{ marginTop: "6px", fontSize: "10px", color: "#9CA3AF" }}>
                        Questions: {v.questions.join(", ")}
                      </div>
                      {showGuidance && TEACHER_GUIDANCE[sk] && (
                        <div style={{ marginTop: "10px" }}>
                          <TeacherGuidancePanel guidance={TEACHER_GUIDANCE[sk]} skillName={meta.name} color={meta.color} />
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </>
        )}

        {/* ── CLASS AGGREGATE VIEW ── */}
        {view === "class" && (
          <>
            <div style={{
              padding: "16px 18px", background: "#FFF", borderRadius: "10px",
              border: "1px solid #E5E7EB", marginBottom: "16px",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <div>
                <div style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>{CLASS_AGGREGATE.class_name}</div>
                <div style={{ fontSize: "12px", color: "#6B7280" }}>{CLASS_AGGREGATE.teacher} · {CLASS_AGGREGATE.student_count} students · {CLASS_AGGREGATE.time_period}</div>
              </div>
              <div style={{ display: "flex", gap: "6px" }}>
                {[
                  { label: "Exceeded", count: CLASS_AGGREGATE.overall.distribution.exceeded, bg: "#ECFDF5", c: "#065F46" },
                  { label: "Met", count: CLASS_AGGREGATE.overall.distribution.met, bg: "#EFF6FF", c: "#1E40AF" },
                  { label: "Nearly", count: CLASS_AGGREGATE.overall.distribution.nearly, bg: "#FFFBEB", c: "#92400E" },
                  { label: "DNM", count: CLASS_AGGREGATE.overall.distribution.did_not, bg: "#FEF2F2", c: "#991B1B" },
                ].map(d => (
                  <div key={d.label} style={{
                    textAlign: "center", padding: "6px 10px", borderRadius: "6px", background: d.bg,
                  }}>
                    <div style={{ fontSize: "16px", fontWeight: 800, color: d.c }}>{d.count}</div>
                    <div style={{ fontSize: "9px", fontWeight: 600, color: d.c, textTransform: "uppercase" }}>{d.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {CLASS_AGGREGATE.skills.map(sk => {
                const meta = SKILLS[sk.id];
                const trendPos = sk.trend.startsWith("+");
                const trendNeg = sk.trend.startsWith("-");
                return (
                  <div key={sk.id} style={{
                    padding: "16px 18px", background: "#FFF", borderRadius: "10px",
                    border: "1px solid #E5E7EB",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
                      <div style={{
                        width: "28px", height: "28px", borderRadius: "6px", background: `${meta.color}14`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "11px", fontWeight: 800, color: meta.color,
                      }}>{meta.num}</div>
                      <span style={{ fontSize: "13px", fontWeight: 600, color: "#111827", flex: 1 }}>{meta.name}</span>
                      <div style={{ display: "flex", gap: "4px" }}>
                        <span style={{ fontSize: "10px", padding: "2px 6px", borderRadius: "3px", fontWeight: 600, background: "#FEF2F2", color: "#991B1B" }}>{sk.below}</span>
                        <span style={{ fontSize: "10px", padding: "2px 6px", borderRadius: "3px", fontWeight: 600, background: "#FFFBEB", color: "#92400E" }}>{sk.at}</span>
                        <span style={{ fontSize: "10px", padding: "2px 6px", borderRadius: "3px", fontWeight: 600, background: "#ECFDF5", color: "#065F46" }}>{sk.above}</span>
                      </div>
                      <span style={{
                        fontSize: "13px", fontWeight: 700, minWidth: "40px", textAlign: "right",
                        color: trendNeg ? "#DC2626" : trendPos ? "#059669" : "#6B7280",
                      }}>{sk.trend}</span>
                    </div>
                    <ScoreBar score={sk.avg} max={sk.max} color={meta.color} />

                    {showGuidance && (
                      <div style={{
                        marginTop: "10px", padding: "12px 14px", background: "#FFFBEB",
                        borderRadius: "6px", borderLeft: `3px solid ${trendNeg ? "#DC2626" : meta.color}`,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                          <span style={{ fontSize: "12px" }}>&#9881;</span>
                          <span style={{ fontSize: "10px", fontWeight: 700, color: "#92400E", textTransform: "uppercase" }}>Class Action</span>
                          {trendNeg && <span style={{ fontSize: "9px", fontWeight: 700, padding: "1px 6px", borderRadius: "3px", background: "#FEE2E2", color: "#991B1B", textTransform: "uppercase" }}>needs attention</span>}
                        </div>
                        <p style={{ margin: 0, fontSize: "12px", color: "#451A03", lineHeight: 1.6 }}>{sk.guidance}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* JSON */}
        {showJson && (
          <div style={{ marginTop: "16px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "6px" }}>
              {view === "class" ? "Class Aggregate API Response" : "Per-Student Challenge API Response"}
            </div>
            <pre style={{
              background: "#111827", color: "#D1D5DB", padding: "18px",
              borderRadius: "10px", fontSize: "11px", lineHeight: 1.5,
              overflow: "auto", maxHeight: "500px", margin: 0,
              fontFamily: "'SF Mono', 'Fira Code', monospace",
            }}>
              {JSON.stringify(view === "class" ? CLASS_AGGREGATE : studentJson, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
